import type { DeleteOutcome } from "../use-entity-writes";

/** A row picked for deletion: its id, and the name the organiser knows it by. */
export interface BulkItem {
  id: string;
  label: string;
}

export interface BulkFailure extends BulkItem {
  error: string;
}

export interface BulkDeleteResult {
  deleted: BulkItem[];
  failed: BulkFailure[];
}

/**
 * Deletes the items one after another, never at the same time, and carries on
 * past a refusal so the result says what happened to every one of them.
 *
 * One at a time because that is how the single delete works and what its
 * backend is built for: an attendee delete starts a cleanup that sweeps the
 * person out of the agenda, the speakers and the directory behind a
 * per-attendee fence, and the list is reloaded once at the end rather than
 * after each. `deleteOne` that throws counts as a refusal with its message.
 */
export async function runBulkDelete(
  items: readonly BulkItem[],
  deleteOne: (id: string) => Promise<DeleteOutcome>,
  onProgress?: (done: number, total: number) => void,
): Promise<BulkDeleteResult> {
  const result: BulkDeleteResult = { deleted: [], failed: [] };
  onProgress?.(0, items.length);
  for (const [index, item] of items.entries()) {
    let outcome: DeleteOutcome;
    try {
      outcome = await deleteOne(item.id);
    } catch (err) {
      outcome = {
        ok: false,
        error: err instanceof Error ? err.message : "Failed to delete",
      };
    }
    if (outcome.ok) result.deleted.push(item);
    else result.failed.push({ ...item, error: outcome.error });
    onProgress?.(index + 1, items.length);
  }
  return result;
}

/**
 * The selection after a run: the ones that failed stay selected, so Delete
 * selected tries exactly them again, and the ones that went are dropped.
 */
export function selectionAfter(
  selected: ReadonlySet<string>,
  result: BulkDeleteResult,
): Set<string> {
  const deleted = new Set(result.deleted.map((item) => item.id));
  return new Set([...selected].filter((id) => !deleted.has(id)));
}

/** The selected ids that are still in the list being shown, in list order. */
export function selectedInView(
  selected: ReadonlySet<string>,
  visible: readonly BulkItem[],
): BulkItem[] {
  return visible.filter((item) => selected.has(item.id));
}

export interface Noun {
  singular: string;
  plural: string;
}

/** "1 attendee", "3 attendees". */
export const countNoun = (n: number, noun: Noun) =>
  `${n} ${n === 1 ? noun.singular : noun.plural}`;

/** "3 attendees deleted. 2 could not be deleted." */
export function describeBulkResult(
  result: BulkDeleteResult,
  noun: Noun,
): string {
  const parts: string[] = [];
  if (result.deleted.length > 0)
    parts.push(`${countNoun(result.deleted.length, noun)} deleted.`);
  if (result.failed.length > 0)
    parts.push(
      `${result.failed.length} could not be deleted and ${
        result.failed.length === 1 ? "is" : "are"
      } still selected.`,
    );
  return parts.join(" ");
}
